/* Product page components, split out of base.js so that every other page of
   the shop stops parsing ~1000 lines it can never use. Loaded from
   sections/main-product.liquid and main-product-gift.liquid, after base.js —
   `togglePageScroll` and friends still come from there. */

class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.cart = document.querySelector("cart-drawer");
    this.alternatePages;
    this.alternatePagesUrls = [];
    this.pagesPromise = null;
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onClickEvent = this.onClickEvent.bind(this);
  }

  connectedCallback() {
    this.productVariants = JSON.parse(this.querySelector("[data-variants-data]").textContent);
    this.productForm = this.querySelector('[id^="ProductForm-"]');

    this.productForm?.addEventListener("submit", this.onFormSubmit);
    this.addEventListener("click", this.onClickEvent);

    if (this.querySelector('[id^="ProductSticky-"]')) {
      this.initStickyBar();
    }

    this.initUgc();
    this.schedulePrefetchPages();
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener("submit", this.onFormSubmit);
    this.removeEventListener("click", this.onClickEvent);
    document.removeEventListener("keydown", this.onCustomerPhotosKeydown);
  }

  async changePage(url) {
    // Normally warm from the idle prefetch; if the shopper is quicker than that,
    // await the same in-flight request rather than dropping the click.
    if (!this.alternatePages) await this.fetchPages();
    if (!this.alternatePages) return;

    const index = this.alternatePagesUrls.indexOf(url);
    const newProductSection = this.alternatePages[index].querySelector("main-product");

    if (newProductSection) {
      const scrollY = window.scrollY;

      this.disconnectedCallback();
      this.innerHTML = newProductSection.innerHTML;
      this.classList = newProductSection.classList.value;
      this.connectedCallback();

      window.history.replaceState({}, '', window.location.origin + this.alternatePagesUrls[index]);

      const newSections = this.alternatePages[index].querySelectorAll("main .shopify-section");
      const currentSections = document.querySelectorAll("main .shopify-section");

      currentSections.forEach((section, i) => {
        const newSection = newSections[i];
        const excludedSections = ["main-product"];

        if (!section.querySelector(excludedSections.join(","))) {
          section.innerHTML = newSection.innerHTML;

          section.querySelectorAll("script").forEach(oldScript => {
            if (!oldScript.src) {
              const newScript = document.createElement("script");
              newScript.textContent = oldScript.textContent;
              section.appendChild(newScript);
              oldScript.remove();
            }
          });
        }
      });

      // Scripts outside main-product (e.g. the bundle card) re-read their data from the new page
      document.dispatchEvent(new CustomEvent("main-product:changed", { detail: { page: this.alternatePages[index] } }));

      window.scrollTo({ top: scrollY, behavior: "instant" });
      requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "instant" }));
    }
  }

  /* Compare pulls the full HTML of every alternate product so it can swap the
     page without a reload. A product page here is around 1 MB, so doing this
     eagerly meant downloading and DOM-parsing megabytes while the hero image and
     the main thread were still busy with the current page.

     It now starts when the browser is idle, and a click awaits the same promise,
     so the behaviour is unchanged — it just no longer competes with the load. */
  fetchPages() {
    if (this.pagesPromise) return this.pagesPromise;

    const toggles = this.querySelectorAll("[data-compare-product]");
    if (toggles.length < 2) return null;

    this.alternatePagesUrls = Array.from(toggles, (toggle) => toggle.dataset.compareProduct);

    this.pagesPromise = Promise.all(this.alternatePagesUrls.map(this.fetchHTML))
      .then((htmlArray) => {
        this.alternatePages = htmlArray;
        return htmlArray;
      })
      .catch((error) => {
        console.error("Error:", error);
        // Let a later click retry instead of failing forever.
        this.pagesPromise = null;
      });

    return this.pagesPromise;
  }

  schedulePrefetchPages() {
    if (this.querySelectorAll("[data-compare-product]").length < 2) return;

    const start = () => this.fetchPages();

    if ("requestIdleCallback" in window) {
      requestIdleCallback(start, { timeout: 5000 });
    } else {
      setTimeout(start, 2000);
    }
  }

  fetchHTML(url) {
    return fetch(url)
    .then(response => {
      if (!response.ok) throw new Error(`Error while fetching page ${url}`);
      return response.text();
    })
    .then(body => {
      return new DOMParser().parseFromString(body, "text/html");
    })
  }

  onFormSubmit(event = false) {
    if (event) event.preventDefault();

    if (this.isLoading) return;

    if (event && event.submitter?.hasAttribute("data-bis")) {
      this.openBis();
      return;
    }

    const sections = [];
    if (this.cart) sections.push("cart-drawer");

    const variantId = parseInt(this.querySelector("[name='id']").value);
    const quantity = parseInt(this.querySelector("[name='quantity']").value);
    const items = [
      {
        id: variantId,
        quantity: quantity
      }
    ];

    this.querySelectorAll(".main-product__upsell-button.active").forEach((btn) => {
      const variantId = parseInt(btn.dataset.upsellVariant);
      if (variantId) items.unshift({ id: variantId, quantity: parseInt(btn.dataset.upsellQuantity) || 1 });
    });

    const giftContainer = this.querySelector("[data-gift-products]");
    if (giftContainer && giftContainer.dataset.selectedVariant) {
      const giftVariantId = parseInt(giftContainer.dataset.selectedVariant);
      // One gift per product; _gift_for links it to the product, so the cart drawer
      // keeps their quantities in sync and removes both together.
      if (giftVariantId) {
        items.unshift({ id: giftVariantId, quantity: quantity, properties: { _gift_for: String(variantId) } });
      }
    }

    const cartData = {
      items: items,
      sections: sections,
      sections_url: window.location.pathname
    };

    this.addToCart(cartData);
  }

  addToCart(data) {
    if (!data) return;

    this.toggleLoading();

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        const error = new Error(response.description);
        throw error;
      } else {
        this.toggleLoading();
        
        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      }
    })
    .catch(error => this.showError(error))
    .finally(() => this.toggleLoading(true));
  }

  onClickEvent(event) {
    const giftAction = event.target.closest('[data-action="toggle-gifts"], [data-action="select-gift"]');
    if (giftAction) {
      event.preventDefault();
      if (giftAction.dataset.action === "toggle-gifts") {
        this.toggleGifts(giftAction.closest("[data-gift-products]"));
      } else {
        this.selectGift(giftAction);
      }
      return;
    }

    if (event.target.hasAttribute("data-action")) {
      switch (event.target.dataset.action) {
        case "open-lightbox":
          this.mountLightbox()?.open(event.target.dataset.slide);
          break;
        case "toggle-video":
        case "toggle-video-sound":
          const videoBlock = event.target.closest(".main-product__gallery-item--video");
          const video = videoBlock.querySelector("video");

          if (event.target.dataset.action == "toggle-video") {
            videoBlock.classList.toggle("playing");

            if (videoBlock.classList.contains("playing")) {
              video.play();
            } else {
              video.pause();
            }
          } else {
            video.muted = !video.muted;
            videoBlock.classList.toggle("muted", video.muted);
          }
          
          break;
        case "change-product":
          const compareToggle = event.target.closest(".main-product__compare-toggle");
          const productOption = event.target.closest("[data-compare-product]");
          compareToggle.querySelector(".main-product__compare-toggle-switcher").classList.toggle("active");
          this.changePage(productOption.getAttribute("data-compare-product"));
          break;
        case "open-compare":
          this.querySelector('[id^="ProductComparePopup-"]').classList.add("open");
          togglePageScroll();
          break;
        case "close-compare":
          this.querySelector('[id^="ProductComparePopup-"]').classList.remove("open");
          togglePageScroll();
          break;
        case "add-compare-product":
          const cartData = {
            items: [
              {
                id: parseInt(event.target.dataset.product),
                quantity: 1
              }
            ],
            sections_url: window.location.pathname
          };

          if (this.cart) {
            cartData["sections"] = ["cart-drawer"];
          };

          this.addToCart(cartData);
          this.querySelector('[id^="ProductComparePopup-"]').classList.remove("open");
          togglePageScroll();
          break;
        case "toggle-disclosure":
          const disclosureHandle = event.target.dataset.disclosure;
          const disclosureContent = this.querySelector(`[data-disclosure-content="${disclosureHandle}"]`);

          event.target.classList.toggle("open");

          if (event.target.classList.contains("open")) {
            disclosureContent.style.maxHeight = disclosureContent.scrollHeight + "px";
          } else {
            disclosureContent.style.maxHeight = null;
          }
          break;
        case "open-ugc":
          this.openUgc(parseInt(event.target.closest("[data-ugc-index]").dataset.ugcIndex));
          break;
        case "close-ugc":
          this.closeUgc();
          break;
        case "prev-ugc":
          this.changeUgc(this.ugcIndex - 1);
          break;
        case "next-ugc":
          this.changeUgc(this.ugcIndex + 1);
          break;
        case "toggle-ugc-sound":
          this.toggleUgcSound();
          break;
        case "toggle-ugc-play":
          this.toggleUgcPlay();
          break;
        case "toggle-bundle":
          this.toggleBundle();
          break;
        case "open-bundle-popup":
          this.openBundlePopup();
          break;
        case "close-bundle-popup":
          this.closeBundlePopup();
          break;
        case "add-bundle":
          this.addBundle();
          break;
        case "toggle-upsell":
          event.target.classList.toggle("active");
          break;
        case "open-upsell-details":
          this.openUpsellPopup(event.target.dataset.upsellId);
          break;
        case "close-upsell-popup":
          this.closeUpsellPopup();
          break;
        case "add-upsell-popup":
          this.addUpsellFromPopup(event.target);
          break;
        case "open-bundle-details-popup":
          this.openBundleDetailsPopup();
          break;
        case "close-bundle-details-popup":
          this.closeBundleDetailsPopup();
          break;
        case "select-bundle-detail":
          this.setActiveBundleDetail(event.target.dataset.bundleDetailIndex);
          break;
        case "close-bis":
          this.closeBis();
          break;
        case "scroll-to-difference":
          const differenceBlock = this.querySelector(".main-product__difference");
          if (differenceBlock) {
            const differenceHeaderHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
            const differenceTop = differenceBlock.getBoundingClientRect().top + window.pageYOffset - differenceHeaderHeight - 24;
            window.scrollTo({ top: differenceTop, behavior: "smooth" });
          }
          break;
      }
    }
  }

  openBis() {
    const popup = this.querySelector("[id^='ProductBis-']");
    if (!popup) return;
    popup.classList.remove("success", "loading");
    popup.hidden = false;
    document.documentElement.style.overflow = "hidden";

    if (!this.bisFormBound) {
      const form = popup.querySelector("form");
      if (form) {
        form.addEventListener("submit", (e) => {
          e.preventDefault();
          this.submitBis();
        });
        this.bisFormBound = true;
      }
    }

    const emailInput = popup.querySelector("input[type='email']");
    setTimeout(() => emailInput?.focus(), 50);
  }

  closeBis() {
    const popup = this.querySelector("[id^='ProductBis-']");
    if (!popup) return;
    popup.hidden = true;
    document.documentElement.style.overflow = null;
  }

  submitBis() {
    const popup = this.querySelector("[id^='ProductBis-']");
    if (!popup) return;
    const emailInput = popup.querySelector("input[type='email']");
    const email = emailInput?.value?.trim();
    if (!email) return;

    const variantId = this.querySelector("[name='id']")?.value;
    if (!variantId) return;

    popup.classList.add("loading");

    fetch("https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=RVj7fw", {
      method: "POST",
      headers: {
        "revision": "2024-02-15",
        "Content-Type": "application/json",
        "X-Klaviyo-Onsite": "1"
      },
      body: JSON.stringify({
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: { data: { type: "profile", attributes: { email } } },
            channels: ["EMAIL"]
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      })
    })
    .then(res => {
      popup.classList.remove("loading");
      if (res.ok) {
        popup.classList.add("success");
      } else {
        console.error("BIS subscribe failed:", res.status);
      }
    })
    .catch(err => {
      popup.classList.remove("loading");
      console.error("BIS subscribe error:", err);
    });
  }

  toggleGifts(container) {
    if (!container) return;
    const toggle = container.querySelector('[data-action="toggle-gifts"]');
    const content = container.querySelector('.main-product__gift-products-content');
    const isOpen = container.classList.toggle("open");
    toggle?.setAttribute("aria-expanded", isOpen ? "true" : "false");

    if (content) {
      content.style.maxHeight = isOpen ? content.scrollHeight + "px" : "";
    }
  }

  selectGift(option) {
    const container = option.closest("[data-gift-products]");
    if (!container) return;

    const variant = option.dataset.variant;

    // Dropdown options and A/B test tiles share the same selection
    container.querySelectorAll('[data-action="select-gift"]').forEach(opt => {
      const isThisOne = opt.dataset.variant === variant;
      opt.classList.toggle("active", isThisOne);
      opt.setAttribute("aria-pressed", isThisOne ? "true" : "false");
    });

    const toggleItem = container.querySelector("[data-gift-toggle]");
    const optionItem = container.querySelector(`.main-product__gift-products-option[data-variant="${variant}"] .main-product__gift-products-item`);
    if (toggleItem && optionItem) {
      toggleItem.innerHTML = optionItem.innerHTML;
    }

    container.dataset.selectedVariant = variant;

    const tile = container.querySelector(`.main-product__gift-tile[data-variant="${variant}"]`);
    if (tile) this.updateGiftTest(container, tile);
  }

  // A/B test (Vlekkenreiniger Pro): selected spray name + value and bottle on the first photo
  updateGiftTest(container, tile) {
    const { giftTitle, giftPrice, giftImage } = tile.dataset;

    const title = container.querySelector("[data-gift-selected-title]");
    const price = container.querySelector("[data-gift-selected-price]");
    if (title) title.textContent = giftTitle;
    if (price) price.textContent = giftPrice;

    const overlayPrice = this.querySelector("[data-gift-overlay-price]");
    const overlayImage = this.querySelector("[data-gift-overlay-image]");
    if (overlayPrice) overlayPrice.textContent = giftPrice;
    if (overlayImage && giftImage) {
      overlayImage.removeAttribute("srcset");
      overlayImage.src = giftImage;
    }
  }

  toggleBundle() {
    const card = this.querySelector(".bundle-offer");
    if (!card) return;

    const formInput = this.productForm?.querySelector("input[name='id']");
    if (!formInput) return;

    card.classList.toggle("active");
    if (card.classList.contains("active")) {
      formInput.dataset.originalId = formInput.value;
      formInput.value = card.dataset.bundleVariant;
    } else if (formInput.dataset.originalId) {
      formInput.value = formInput.dataset.originalId;
    }
  }

  openBundlePopup() {
    const popup = this.querySelector('[id^="BundleOfferPopup-"]');
    if (!popup) return;

    popup.classList.add("open");
    togglePageScroll();
  }

  closeBundlePopup() {
    const popup = this.querySelector('[id^="BundleOfferPopup-"]');
    if (!popup) return;

    popup.classList.remove("open");
    togglePageScroll();
  }

  addBundle() {
    const popup = this.querySelector('[id^="BundleOfferPopup-"]');
    const button = popup?.querySelector("[data-action='add-bundle']");
    if (!button) return;

    const bundleVariantId = parseInt(button.dataset.bundleVariant);
    if (!bundleVariantId) return;

    const cartData = {
      items: [{ id: bundleVariantId, quantity: 1 }],
      sections_url: window.location.pathname
    };

    if (this.cart) {
      cartData.sections = ["cart-drawer"];
    }

    this.addToCart(cartData);
    this.closeBundlePopup();
  }

  openUpsellPopup(upsellId) {
    const popup = this.querySelector('[id^="ProductUpsellPopup-"]');
    if (!popup || !upsellId) return;

    popup.querySelectorAll("[data-upsell-id]").forEach((item) => {
      item.classList.toggle("active", item.dataset.upsellId === upsellId);
    });

    popup.classList.add("open");
    togglePageScroll();
  }

  closeUpsellPopup() {
    const popup = this.querySelector('[id^="ProductUpsellPopup-"]');
    if (!popup) return;

    popup.classList.remove("open");
    togglePageScroll();
  }

  addUpsellFromPopup(button) {
    const variantId = parseInt(button.dataset.product);
    if (!variantId) return;

    const cartData = {
      items: [{ id: variantId, quantity: parseInt(button.dataset.quantity) || 1 }],
      sections_url: window.location.pathname
    };

    if (this.cart) {
      cartData.sections = ["cart-drawer"];
    }

    this.addToCart(cartData);
    this.closeUpsellPopup();
  }

  openBundleDetailsPopup() {
    const popup = this.querySelector('[id^="ProductBundleDetailsPopup-"]');
    if (!popup) return;

    popup.classList.add("open");
    togglePageScroll();
  }

  closeBundleDetailsPopup() {
    const popup = this.querySelector('[id^="ProductBundleDetailsPopup-"]');
    if (!popup) return;

    popup.classList.remove("open");
    togglePageScroll();
  }

  setActiveBundleDetail(index) {
    const popup = this.querySelector('[id^="ProductBundleDetailsPopup-"]');
    if (!popup || index == null) return;

    popup.querySelectorAll("[data-bundle-detail-media]").forEach((el) => {
      el.classList.toggle("active", el.dataset.bundleDetailMedia === index);
    });
    popup.querySelectorAll("[data-bundle-detail-content]").forEach((el) => {
      el.classList.toggle("active", el.dataset.bundleDetailContent === index);
    });
    popup.querySelectorAll("[data-bundle-detail-index]").forEach((el) => {
      el.classList.toggle("active", el.dataset.bundleDetailIndex === index);
    });
  }

  resetUgcBar(bar, width) {
    bar.classList.add("no-transition");
    bar.querySelector("span").style.width = width;
    bar.offsetHeight;
    bar.classList.remove("no-transition");
  }

  toggleUgcPlay() {
    const slide = this.ugcSlides[this.ugcIndex];
    if (!slide) return;
    const video = slide.querySelector("video");
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  initUgc() {
    this.ugcPopup = this.querySelector('[id^="ProductUgcPopup-"]');
    if (!this.ugcPopup) return;

    this.ugcSlides = this.ugcPopup.querySelectorAll(".main-product__ugc-popup-slide");
    this.ugcProgressBars = this.ugcPopup.querySelectorAll(".main-product__ugc-popup-progress-bar");
    this.ugcTitleEl = this.ugcPopup.querySelector("[data-ugc-title]");
    this.ugcIndex = 0;
    this.ugcMuted = false;

    this.ugcSlides.forEach((slide) => {
      const video = slide.querySelector("video");
      video.addEventListener("ended", () => this.onUgcEnded());
      video.addEventListener("timeupdate", () => this.onUgcTimeUpdate(slide, video));
    });

    const circleVideos = this.querySelectorAll(".main-product__ugc-video");
    if (circleVideos.length && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) video.src = video.dataset.src;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { rootMargin: "200px" });

      circleVideos.forEach((video) => observer.observe(video));
    }
  }

  openUgc(index) {
    if (!this.ugcPopup) return;

    this.ugcPopup.classList.add("open");
    togglePageScroll();
    this.changeUgc(index);
  }

  closeUgc() {
    if (!this.ugcPopup) return;

    this.ugcPopup.classList.remove("open");
    togglePageScroll();

    this.ugcSlides.forEach((slide) => {
      const video = slide.querySelector("video");
      video.pause();
      video.currentTime = 0;
      slide.classList.remove("active");
    });

    this.ugcProgressBars.forEach((bar) => {
      bar.classList.remove("watched");
      this.resetUgcBar(bar, "0%");
    });
  }

  changeUgc(index) {
    if (!this.ugcPopup) return;
    if (index < 0) return;
    if (index >= this.ugcSlides.length) return this.closeUgc();

    const prevSlide = this.ugcSlides[this.ugcIndex];
    if (prevSlide) {
      const prevVideo = prevSlide.querySelector("video");
      prevVideo.pause();
      prevVideo.currentTime = 0;
      prevSlide.classList.remove("active");
    }

    this.ugcIndex = index;

    this.ugcProgressBars.forEach((bar, i) => {
      bar.classList.toggle("watched", i < index);
      if (i !== index) {
        this.resetUgcBar(bar, i < index ? "100%" : "0%");
      } else {
        this.resetUgcBar(bar, "0%");
      }
    });

    this.ugcPopup.querySelector(".main-product__ugc-popup-nav--prev").classList.toggle("is-disabled", index === 0);
    this.ugcPopup.querySelector(".main-product__ugc-popup-nav--next").classList.toggle("is-disabled", index === this.ugcSlides.length - 1);

    const slide = this.ugcSlides[index];
    const video = slide.querySelector("video");
    slide.classList.add("active");
    this.ugcTitleEl.innerText = video.dataset.title || "";
    video.muted = this.ugcMuted;
    video.currentTime = 0;
    video.play().catch(() => {});
  }

  onUgcEnded() {
    const currentBar = this.ugcProgressBars[this.ugcIndex];
    if (currentBar) {
      currentBar.classList.add("watched");
      currentBar.querySelector("span").style.width = "100%";
    }
    this.changeUgc(this.ugcIndex + 1);
  }

  onUgcTimeUpdate(slide, video) {
    if (!slide.classList.contains("active")) return;
    if (!video.duration) return;

    const bar = this.ugcProgressBars[this.ugcIndex];
    if (!bar) return;

    const percent = (video.currentTime / video.duration) * 100;
    bar.querySelector("span").style.width = `${percent}%`;
  }

  toggleUgcSound() {
    if (!this.ugcPopup) return;

    this.ugcMuted = !this.ugcMuted;
    this.ugcPopup.classList.toggle("muted", this.ugcMuted);

    const currentSlide = this.ugcSlides[this.ugcIndex];
    if (currentSlide) {
      currentSlide.querySelector("video").muted = this.ugcMuted;
    }
  }

  /* The lightbox ships inside a <template> to keep it out of the initial DOM.
     Clone it on first use; afterwards the live element is reused. */
  mountLightbox() {
    const existing = this.querySelector("product-lightbox");
    if (existing) return existing;

    const template = this.querySelector("[data-lightbox-template]");
    if (!template) return null;

    template.parentNode.insertBefore(template.content.cloneNode(true), template);
    return this.querySelector("product-lightbox");
  }

  initStickyBar() {
    this.stickyBar = this.querySelector('[id^="ProductSticky-"]');
    this.productSubmit = this.querySelector('[id^="ProductSubmit-"]');

    const needToShow = !this.isInViewport(this.productSubmit);
    this.stickyBar.classList.toggle("show", needToShow);
    this.syncStickyHeight();

    if (this.stickyBarBound) return;
    this.stickyBarBound = true;

    window.addEventListener("scroll", () => {
      this.stickyBar = this.querySelector('[id^="ProductSticky-"]');
      this.productSubmit = this.querySelector('[id^="ProductSubmit-"]');
      if (!this.stickyBar || !this.productSubmit) return;
      this.stickyBar.classList.toggle("show", (!this.isInViewport(this.productSubmit)));
      this.syncStickyHeight();
    })
  }

  // Space the visible sticky bar takes at the bottom of the viewport (its
  // height + bottom offset), so fixed elements (e.g. the compact cookie
  // banner A/B variant) can sit above it.
  syncStickyHeight() {
    const visible = this.stickyBar && this.stickyBar.classList.contains("show");
    const height = visible
      ? Math.round(this.stickyBar.offsetHeight + (parseFloat(getComputedStyle(this.stickyBar).bottom) || 0))
      : 0;
    if (height === this.stickyHeight) return;
    this.stickyHeight = height;
    document.documentElement.style.setProperty("--product-sticky-height", `${height}px`);
  }

  showError(error) {
    console.error(error);

    if (!this.querySelector("[product-notification]")) return;

    const messageBlock = this.querySelector("[product-notification]");

    messageBlock.innerText = error.message;
    setTimeout(() => {messageBlock.innerText = ''}, 5000);
    messageBlock.scrollIntoView({behavior: "smooth",block:"center"});
  }

  toggleLoading(turnOff = false) {
    this.isLoading = !this.isLoading;
    if (turnOff) this.isLoading = false;
    this.classList.toggle("loading", this.isLoading);
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < viewportHeight &&
      rect.left < viewportWidth
    );
  };
}

if (document.querySelector("main-product") && !customElements.get("main-product")) {
  customElements.define("main-product", MainProduct);
}

class ProductLightbox extends HTMLElement {
  constructor() {
    super();
    this.onSliderUpdate = this.onSliderUpdate.bind(this);
    this.onClickEvent = this.onClickEvent.bind(this);
  }
  
  connectedCallback() {
    this.addEventListener("slider-updated", this.onSliderUpdate);
    this.addEventListener("click", this.onClickEvent);
  }

  disconnectedCallback() {
    this.removeEventListener("slider-updated", this.onSliderUpdate);
    this.removeEventListener("click", this.onClickEvent);
  }

  onClickEvent(event) {
    if (event.target.hasAttribute("data-action")) {
      switch (event.target.dataset.action) {
        case "select-slide":
          this.querySelector('.lightbox__gallery').scrollToSlide(event.target.dataset.index);
          break;
        case "close":
          this.close();
          break;
      }
    }
  }

  onSliderUpdate(event) {
    this.querySelectorAll('[id^="Slider-LightboxNav-"] li').forEach(slide => {
      const index = parseInt(slide.dataset.index);
      slide.classList.toggle("selected", event.detail.currentSlide === index);
      if (event.detail.currentSlide === index) {
        slide.scrollIntoView({behavior: "smooth"});
      }
    })
  }

  open(selectedSlide = false) {
    this.classList.add("open");
    togglePageScroll();

    if (selectedSlide) {
      this.querySelector('.lightbox__gallery').scrollToSlide(selectedSlide);
    }
  }

  close() {
    this.classList.remove("open");
    togglePageScroll();
  }
}

// The element usually lives inside a <template>, where querySelector can't see
// it, so the template itself has to count as a reason to register the class.
if (document.querySelector("product-lightbox, [data-lightbox-template]") && !customElements.get("product-lightbox")) {
  customElements.define("product-lightbox", ProductLightbox);
}
